import { Link } from '@tanstack/react-router';
import { ArrowRight, ClipboardCheck, MessageSquareReply, Send, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import {
  MANAGER_NO_REASON_CODE,
  MANAGER_RESPONSE_LABELS,
  reasonsForStages,
  shortlistCounts,
  type JobDetail,
  type ShortlistCandidate,
  type ShortlistView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import type { Column } from '../../../components/data-table/types';
import { Popover, usePopover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { MANAGER_RESPONSE_TONES } from '../../../components/status-tones';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { formatDate } from '../../../lib/format';
import { useAppSettings } from '../../../lib/app-settings';
import { useModule } from '../../../lib/modules';
import { EMPTY_SELECTION, isSelected, type SelectionState } from '../../../lib/selection';
import { useCanEdit } from '../../../stores/session';
import { usePipelineOptions } from '../../pipeline/api/pipeline-api';
import { validatePipelineSearch, type PipelineSearch } from '../../pipeline/pipeline-search';
import { WhyThisScore } from '../../ranking/components/WhyThisScore';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import { mayGoOn, maySendTestFromShortlist, proceedHint } from '../proceed-rule';
import { useAnswerShortlist, useProceedShortlist, useRejectWithReason, useSendShortlist, useSendTestFromShortlist, useShortlist } from '../api/shortlist-api';
import { AddToCvBankButton } from '../../cv-bank/components/AddToCvBankButton';
import { AnswerControls } from './AnswerControls';
import { usePhoneScreen } from '../../phone-screen/api/phone-screen-api';
import { PhoneScreenSwitch } from '../../phone-screen/components/PhoneScreenSwitch';
import styles from './shortlist.module.css';
import fit from '../../../components/FitPage.module.css';

// R6 — stage ⑤ Shortlist on JOB-12: SHL-02, HR's view of what was sent and what the manager said
// (§L31, F24.01, F24.05). N-91 opens here. A manager's No is never a rejection by itself: "Reject
// the No's" opens the reason picker with SCR-HM-NO already picked.

interface StageProps {
  readonly detail: JobDetail;
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
}

export function ShortlistStage({ detail, search, onSearch }: StageProps): ReactNode {
  const shortlist = useShortlist(detail.id, 'hr');
  if (shortlist.isPending) return <SkeletonLines count={8} />;
  if (shortlist.error?.status === 403) return <ForbiddenState resource="this shortlist" contact="Your admin" isInset />;
  if (shortlist.error || !shortlist.data) return <ServerErrorState onRetry={() => void shortlist.refetch()} />;
  return <HrShortlist detail={detail} view={shortlist.data} search={search} onSearch={onSearch} />;
}

function HrShortlist({ detail, view, search, onSearch }: StageProps & { readonly view: ShortlistView }): ReactNode {
  const jobId = detail.id;
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [rejecting, setRejecting] = useState<{ readonly people: readonly ShortlistCandidate[]; readonly anchor: AnchorRect; readonly preselect?: string } | null>(null);
  const [changing, setChanging] = useState<{ readonly candidate: ShortlistCandidate; readonly anchor: AnchorRect } | null>(null);
  const { anchor: sendAnchor, triggerRef: sendTrigger, toggle: toggleSend, close: closeSend } = usePopover();
  const { send, isSending } = useSendShortlist(jobId);
  // ADR 0079: with phone Screening on, going ahead lands in Screening, not Contact.
  const phoneScreen = usePhoneScreen(jobId);
  const isScreeningOn = phoneScreen.data?.isOn === true;
  const nextStep = isScreeningOn ? 'Screening' : 'Contact';
  // ADR 0082 (owner 2026-11-01): off by default — nothing goes to the manager, and HR moves the shortlisted straight on.
  const isManagerStepOn = useModule('manager_shortlist');
  const { proceed, pending: proceeding } = useProceedShortlist(jobId, nextStep, isManagerStepOn);
  const rejection = useRejectWithReason(jobId);
  const { answer, pending: answering } = useAnswerShortlist(jobId);
  const options = usePipelineOptions(view.viewer.mayReject);
  const isPoolsOn = useModule('pools');
  const mayPool = useCanEdit('talent_pool');
  const reasons = useMemo(() => reasonsForStages(options.data?.reasons ?? [], ['screening']), [options.data?.reasons]);
  const counts = shortlistCounts(view.candidates);
  const selected = view.candidates.filter((candidate) => isSelected(selection, candidate.applicationId));
  const noes = view.candidates.filter((candidate) => candidate.answer === 'no');
  const waitingSelected = selected.filter((candidate) => mayGoOn(candidate, isManagerStepOn));
  const proceedOff = proceedHint(selected, isScreeningOn, isManagerStepOn);
  // ADR 0047: off until an HR Lead switches it on; a paper test is booked from Contact instead.
  const isSendTestOn = useAppSettings().data?.settings.sendTestFromShortlist === true;
  const isByHand = detail.assessment.settings.isByHand;
  const hasSendableTest = detail.assessment.isOn && (isByHand || (detail.assessment.test !== null && !detail.assessment.test.isOffline));
  const isAssessmentsOn = useModule('assessments');
  const canSendTest = isSendTestOn && isAssessmentsOn && hasSendableTest && view.viewer.mayProceed;
  const { sendTest } = useSendTestFromShortlist(jobId, isByHand);
  const testReady = selected.filter(maySendTestFromShortlist);
  const sendTestLabel = isByHand ? 'Move to Assessment' : 'Send test';
  const isPending = (id: string): boolean => proceeding.has(id) || rejection.pending.has(id) || answering.has(id);
  const open = (applicationId: string): void => onSearch(validatePipelineSearch({ ...search, app: applicationId }));

  const allColumns: readonly Column<ShortlistCandidate>[] = [
    {
      id: 'name',
      header: 'Candidate',
      isIdentity: true,
      defaultWidth: 220,
      exportValue: (row) => row.candidate.fullName,
      cell: (row) => (
        <button type="button" className={styles.nameButton} onClick={() => open(row.applicationId)}>
          {row.candidate.fullName}
        </button>
      ),
    },
    { id: 'score', header: 'Match', isNumeric: true, defaultWidth: 80, exportValue: (row) => row.score, cell: (row) => row.score ?? '—' },
    { id: 'hr', header: 'HR ★', isNumeric: true, defaultWidth: 80, exportValue: (row) => row.hrStars, cell: (row) => (row.hrStars === null ? '—' : row.hrStars.toFixed(1)) },
    { id: 'sent', header: 'Sent on', defaultWidth: 120, exportValue: (row) => row.sentAt, cell: (row) => (row.sentAt ? formatDate(row.sentAt) : '—') },
    {
      id: 'answer',
      header: 'Answer',
      defaultWidth: 150,
      exportValue: (row) => MANAGER_RESPONSE_LABELS[row.answer],
      cell: (row) => (
        <Chip tone={MANAGER_RESPONSE_TONES[row.answer]} plain={row.answer !== 'awaiting'}>
          {isPending(row.applicationId) ? 'Saving…' : MANAGER_RESPONSE_LABELS[row.answer]}
        </Chip>
      ),
    },
    {
      id: 'reason',
      header: 'Reason',
      defaultWidth: 220,
      exportValue: (row) => [row.reason, row.reasonLabel].filter(Boolean).join(' — ') || null,
      cell: (row) => (row.reason ? `${row.reason}${row.reasonLabel ? ` (${row.reasonLabel})` : ''}` : '—'),
    },
    { id: 'comment', header: 'Comment', defaultWidth: 220, exportValue: (row) => row.comment, cell: (row) => row.comment ?? '—' },
    {
      id: 'by',
      header: 'By whom',
      defaultWidth: 180,
      exportValue: (row) => row.answeredByName,
      cell: (row) => (row.answeredByName ? `${row.answeredByName}${row.isOnBehalf ? ' (HR)' : ''}` : '—'),
    },
  ];
  // §L28: with the manager step off its columns go; answers already given stay stored.
  const columns = isManagerStepOn ? allColumns : allColumns.filter((column) => !MANAGER_COLUMNS.includes(column.id));

  if (view.candidates.length === 0) {
    return (
      <EmptyState
        title="Nobody shortlisted yet"
        body={
          isManagerStepOn
            ? 'Shortlist people on ④ Review — press A in the review card, or drag them to Shortlisted — then send them from here.'
            : `Shortlist people on ④ Review — tick them and press Shortlist, or press A on a row — then move them to ${nextStep.toLowerCase()} from here.`
        }
        action={
          <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'review' }} className={styles.action}>
            Go to review
          </Link>
        }
        isInset
      />
    );
  }

  return (
    <div className={`${styles.stage} ${fit.fill}`}>
      <div className={styles.toolbar}>
        {isManagerStepOn && (
        <div className={styles.recipients}>
          {view.recipients.length > 0 ? (
            <>
              Goes to <strong>{view.recipients.map((recipient) => recipient.fullName).join(' and ')}</strong>
            </>
          ) : (
            <>
              Nobody to send to yet.{' '}
              <Link to="/jobs/$jobId/setup/$section" params={{ jobId, section: 'basics' }} className={styles.inlineLink}>
                Name the Requested by and Line manager
              </Link>
            </>
          )}
        </div>
        )}
        {phoneScreen.data && view.viewer.mayProceed && <PhoneScreenSwitch jobId={jobId} view={phoneScreen.data} />}
        {isManagerStepOn && (
        <ul className={styles.counts} aria-label="Answers so far">
          {(['not_sent', 'awaiting', 'yes', 'no', 'hr_proceeded'] as const).map((answerKey) => (
            <li key={answerKey}>
              <Chip tone={MANAGER_RESPONSE_TONES[answerKey]} plain={answerKey !== 'awaiting'}>
                {MANAGER_RESPONSE_LABELS[answerKey]} {countOf(counts, answerKey)}
              </Chip>
            </li>
          ))}
        </ul>
        )}
        <div className={styles.actions}>
          {isManagerStepOn && view.viewer.maySend && (
            <Button ref={sendTrigger} variant="solid" icon={Send} disabled={counts.notSent === 0 || view.recipients.length === 0 || isSending} onClick={toggleSend}>
              {counts.notSent === 0 ? 'Nothing new to send' : view.sends.length === 0 ? `Send shortlist (${counts.notSent})` : `Send ${counts.notSent} more`}
            </Button>
          )}
          {isManagerStepOn && view.viewer.mayReject && noes.length > 0 && (
            <Button
              variant="danger"
              icon={X}
              onClick={(event) => setRejecting({ people: noes, anchor: anchorFrom(event.currentTarget), preselect: MANAGER_NO_REASON_CODE })}
            >
              Reject the No's ({noes.length})
            </Button>
          )}
        </div>
      </div>

      <ShortlistCaption count={view.candidates.length} />

      <DataTable
        screenId="shortlist"
        label="Shortlist"
        columns={columns}
        rows={view.candidates}
        getRowId={(row) => row.applicationId}
        sort={[]}
        onSortChange={() => undefined}
        selection={selection}
        onSelectionChange={setSelection}
        isSelectable={view.viewer.mayProceed}
        onOpenRow={(row) => open(row.applicationId)}
        toneOf={isManagerStepOn ? (row) => MANAGER_RESPONSE_TONES[row.answer] : undefined}
        rowActions={(row) => (
          <span className={styles.rowActions}>
            {canSendTest && maySendTestFromShortlist(row) && (
              <Button size="sm" variant="ghost" icon={ClipboardCheck} onClick={() => void sendTest([row])}>
                {sendTestLabel}
              </Button>
            )}
            {mayGoOn(row, isManagerStepOn) && view.viewer.mayProceed && (
              <Button size="sm" variant="ghost" icon={ArrowRight} onClick={() => void proceed([row])}>
                Move to {nextStep.toLowerCase()}
              </Button>
            )}
            {isManagerStepOn && mayChange(view, row) && (
              <Button size="sm" variant="ghost" icon={MessageSquareReply} onClick={(event) => setChanging({ candidate: row, anchor: anchorFrom(event.currentTarget) })}>
                Change answer
              </Button>
            )}
          </span>
        )}
        bulkActions={
          <>
            <Button
              size="sm"
              variant="outline"
              icon={ArrowRight}
              disabled={proceedOff !== null}
              title={proceedOff ?? undefined}
              onClick={() => void proceed(waitingSelected).then(() => setSelection(EMPTY_SELECTION))}
            >
              {waitingSelected.length > 0 && waitingSelected.length < selected.length ? `Move ${waitingSelected.length} to ${nextStep.toLowerCase()}` : `Move to ${nextStep.toLowerCase()}`}
            </Button>
            {canSendTest && (
              <Button
                size="sm"
                variant="outline"
                icon={ClipboardCheck}
                disabled={testReady.length === 0}
                title={testReady.length === 0 ? 'Only people not sent yet, or waiting for the manager, can skip ahead.' : undefined}
                onClick={() => void sendTest(testReady).then(() => setSelection(EMPTY_SELECTION))}
              >
                {sendTestLabel}
                {testReady.length > 0 ? ` (${testReady.length})` : ''}
              </Button>
            )}
            <Button size="sm" variant="danger" icon={X} onClick={(event) => setRejecting({ people: selected, anchor: anchorFrom(event.currentTarget) })}>
              Reject…
            </Button>
            <AddToCvBankButton people={selected.map((person) => ({ candidateId: person.candidate.id, name: person.candidate.fullName }))} onDone={() => setSelection(EMPTY_SELECTION)} />
            {proceedOff && <span className={styles.bulkHint}>{proceedOff}</span>}
          </>
        }
      />

      {isManagerStepOn && <SendHistory view={view} />}

      <Popover anchor={sendAnchor} onClose={closeSend} label="Send the shortlist" align="right">
        <div className={styles.confirm}>
          <p className={styles.confirmText}>
            Send {counts.notSent} {counts.notSent === 1 ? 'person' : 'people'} to {view.recipients.map((recipient) => recipient.fullName).join(' and ')}? They get one email each
            {view.sends.length > 0 ? ' naming only the new people' : ''}.
          </p>
          <Button
            variant="solid"
            icon={Send}
            disabled={isSending}
            onClick={() => {
              closeSend();
              void send();
            }}
          >
            Send
          </Button>
        </div>
      </Popover>

      <Popover anchor={rejecting?.anchor ?? null} onClose={() => setRejecting(null)} label="Reject" align="left">
        {rejecting && (
          <ReasonPicker
            key={rejecting.people.map((person) => person.applicationId).join(',')}
            mode="reject"
            reasons={reasons}
            pools={options.data?.pools ?? []}
            canPool={isPoolsOn && mayPool}
            contactAttempts={0}
            candidateName={rejecting.people.length === 1 && rejecting.people[0] ? rejecting.people[0].candidate.fullName : `${rejecting.people.length} people`}
            initialReasonCode={rejecting.preselect}
            canAddToCvBank={rejecting.people.some((person) => person.cvDocumentId !== null)}
            onConfirm={(choice) => {
              const people = rejecting.people.map((person) => ({ applicationId: person.applicationId, name: person.candidate.fullName }));
              setRejecting(null);
              setSelection(EMPTY_SELECTION);
              void rejection.reject(people, choice.reasonCode, { poolIds: choice.poolIds, newPoolNames: choice.newPoolNames, addToCvBank: choice.addToCvBank });
            }}
          />
        )}
      </Popover>

      <Popover anchor={changing?.anchor ?? null} onClose={() => setChanging(null)} label="Change the answer" align="right">
        {changing && (
          <div className={styles.changePanel}>
            <p className={styles.confirmText}>
              {changing.candidate.candidate.fullName}: {MANAGER_RESPONSE_LABELS[changing.candidate.answer]}
              {changing.candidate.answeredByName ? `, from ${changing.candidate.answeredByName}` : ''}. Your change is recorded with your name.
            </p>
            <AnswerControls
              candidate={changing.candidate}
              reasons={view.reasons}
              isPending={answering.has(changing.candidate.applicationId)}
              onAnswer={async (choice) => {
                const isSaved = await answer(changing.candidate, choice);
                if (isSaved) setChanging(null);
                return isSaved;
              }}
            />
          </div>
        )}
      </Popover>

      <WhyThisScore applicationId={search.app ?? null} jobId={jobId} onClose={() => onSearch(validatePipelineSearch({ ...search, app: undefined }))} />
    </div>
  );
}

/** The manager's side of the table, gone while "Shortlist to the line manager" is off (ADR 0082). */
const MANAGER_COLUMNS: readonly string[] = ['sent', 'answer', 'reason', 'comment', 'by'];

/** SHL-02 (Job-shortlist.dc.html): how many, and the order the server sends them in — match score, highest first. */
export function ShortlistCaption({ count }: { readonly count: number }): ReactNode {
  return (
    <p className={styles.caption}>
      <span className={styles.captionCount}>
        {count} {count === 1 ? 'person' : 'people'}
      </span>
      {' · Best fit first'}
    </p>
  );
}

/** Each send and its Done: who sent whom, and when the manager finished (F24.01, F24.03). */
function SendHistory({ view }: { readonly view: ShortlistView }): ReactNode {
  if (view.sends.length === 0) return null;
  return (
    <section className={styles.history} aria-label="Sends">
      <h3 className={styles.historyTitle}>Sends</h3>
      <ol className={styles.historyList}>
        {view.sends.map((send) => (
          <li key={send.id}>
            {formatDate(send.sentAt)}: {send.applicationCount} {send.applicationCount === 1 ? 'person' : 'people'}
            {send.sentByName ? ` by ${send.sentByName}` : ''}
            {send.completedAt ? ` — Done ${formatDate(send.completedAt)}${send.completedByName ? ` by ${send.completedByName}` : ''}` : ' — waiting for Done'}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** §L31 Q6: HR changes an answer after Done; an HR Lead may answer for the manager before it. */
function mayChange(view: ShortlistView, row: ShortlistCandidate): boolean {
  if (row.answer === 'not_sent' || row.answer === 'hr_proceeded') return false;
  if (view.viewer.actor === 'hr_lead') return true;
  return view.viewer.actor === 'recruiter' && row.isLocked && (row.answer === 'yes' || row.answer === 'no');
}

function countOf(counts: ReturnType<typeof shortlistCounts>, answer: ShortlistCandidate['answer']): number {
  switch (answer) {
    case 'not_sent':
      return counts.notSent;
    case 'awaiting':
      return counts.awaiting;
    case 'yes':
      return counts.yes;
    case 'no':
      return counts.no;
    case 'hr_proceeded':
      return counts.proceeded;
  }
}
